import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  notificationsApi,
  type NotificationItem,
  type NotificationsPage,
} from '@/api/notifications'
import { useAuth } from '@/context/AuthContext'

const PAGE_SIZE = 20
const POLL_INTERVAL_MS = 10_000
const ALERT_SOUND_URL = '/sound/alert.mp3'

type NotificationsContextValue = {
  page: NotificationsPage | null
  items: NotificationItem[]
  unreadCount: number
  loading: boolean
  marking: boolean
  pollIntervalMs: number
  reloadNotifications: (silent?: boolean) => void
  markAllRead: () => Promise<void>
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, token } = useAuth()
  const [page, setPage] = useState<NotificationsPage | null>(null)
  const [loading, setLoading] = useState(false)
  const [marking, setMarking] = useState(false)
  const previousUnreadCountRef = useRef<number | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const playAlert = useCallback(() => {
    if (typeof Audio === 'undefined') return
    if (!audioRef.current) {
      audioRef.current = new Audio(ALERT_SOUND_URL)
      audioRef.current.preload = 'auto'
    }
    const audio = audioRef.current
    audio.currentTime = 0
    void audio.play().catch(() => {
      // Browser can block autoplay until the first user gesture.
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const unlockAudio = () => {
      if (!audioRef.current) {
        audioRef.current = new Audio(ALERT_SOUND_URL)
        audioRef.current.preload = 'auto'
      }
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('keydown', unlockAudio)
    }

    window.addEventListener('pointerdown', unlockAudio, { once: true })
    window.addEventListener('keydown', unlockAudio, { once: true })
    return () => {
      window.removeEventListener('pointerdown', unlockAudio)
      window.removeEventListener('keydown', unlockAudio)
    }
  }, [])

  const applyPage = useCallback((nextPage: NotificationsPage | null) => {
    setPage(nextPage)
    const nextUnreadCount = nextPage?.unread_count ?? 0
    const previousUnreadCount = previousUnreadCountRef.current
    if (previousUnreadCount != null && nextUnreadCount > previousUnreadCount) {
      playAlert()
    }
    previousUnreadCountRef.current = nextUnreadCount
  }, [playAlert])

  const reloadNotifications = useCallback((silent = false) => {
    if (!isAuthenticated || !token) {
      setPage(null)
      setLoading(false)
      previousUnreadCountRef.current = null
      return
    }

    if (!silent) setLoading(true)
    notificationsApi
      .getNotifications({ page_size: PAGE_SIZE })
      .then(applyPage)
      .catch(() => applyPage(null))
      .finally(() => {
        if (!silent) setLoading(false)
      })
  }, [applyPage, isAuthenticated, token])

  useEffect(() => {
    reloadNotifications()
  }, [reloadNotifications])

  useEffect(() => {
    if (!isAuthenticated || !token) return
    const timer = window.setInterval(() => reloadNotifications(true), POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [isAuthenticated, reloadNotifications, token])

  const markAllRead = useCallback(async () => {
    setMarking(true)
    try {
      await notificationsApi.markRead()
      reloadNotifications(true)
    } finally {
      setMarking(false)
    }
  }, [reloadNotifications])

  const items = page?.results ?? []
  const unreadCount = page?.unread_count ?? 0

  const value = useMemo<NotificationsContextValue>(
    () => ({
      page,
      items,
      unreadCount,
      loading,
      marking,
      pollIntervalMs: POLL_INTERVAL_MS,
      reloadNotifications,
      markAllRead,
    }),
    [items, loading, markAllRead, marking, page, reloadNotifications, unreadCount]
  )

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}

export const useNotifications = (): NotificationsContextValue => {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider')
  }
  return context
}
